import logger from "../utils/logger";
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogTrigger,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { CreditCard, Plus } from 'lucide-react';

import API_URL from '../utils/api';

const Advances = () => {
    const [advances, setAdvances] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        employee_id: '',
        amount: '',
        reason: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [advancesRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/advances`, { withCredentials: true }),
                axios.get(`${API_URL}/api/employees`, { withCredentials: true })
            ]);
            setAdvances(advancesRes.data);
            setEmployees(empRes.data.filter(e => e.role !== 'admin'));
        } catch (error) {
            logger.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                amount: parseFloat(formData.amount)
            };

            await axios.post(`${API_URL}/api/advances`, payload, { withCredentials: true });
            setIsDialogOpen(false);
            setFormData({ employee_id: '', amount: '', reason: '' });
            fetchData();
        } catch (error) {
            alert(error.response?.data?.detail || 'Error creating advance');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-slate-500">Loading advances...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="advances-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Salary Advances
                    </h1>
                    <p className="text-slate-500 mt-1">Manage employee salary advances</p>
                </div>
                
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-brand hover:bg-brand rounded-none" data-testid="add-advance-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Create Advance
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-none">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                Create Salary Advance
                            </DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">Request a salary advance with amount and repayment plan.</DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Employee</Label>
                                <Select value={formData.employee_id} onValueChange={(v) => setFormData({...formData, employee_id: v})}>
                                    <SelectTrigger className="rounded-none mt-1" data-testid="advance-employee-select">
                                        <SelectValue placeholder="Select employee" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-none">
                                        {employees.map(emp => (
                                            <SelectItem key={emp._id} value={emp._id}>
                                                {emp.name} ({emp.employee_id})
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Advance Amount</Label>
                                <Input
                                    type="number"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                                    className="rounded-none mt-1"
                                    required
                                    data-testid="advance-amount-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reason</Label>
                                <Textarea
                                    value={formData.reason}
                                    onChange={(e) => setFormData({...formData, reason: e.target.value})}
                                    className="rounded-none mt-1"
                                    rows={3}
                                    required
                                    data-testid="advance-reason-input"
                                />
                            </div>
                            <div className="flex justify-end gap-3 pt-4">
                                <Button type="button" variant="outline" className="rounded-none" onClick={() => setIsDialogOpen(false)}>
                                    Cancel
                                </Button>
                                <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="advance-save-btn">
                                    Create Advance
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Advances Table */}
            <Card className="rounded-none border-slate-200">
                <CardHeader className="border-b border-slate-200">
                    <CardTitle className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        <CreditCard className="h-5 w-5 text-brand" />
                        All Advances
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="advances-table">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50">
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Employee</th>
                                    <th className="text-right p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Amount</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Reason</th>
                                    <th className="text-left p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                                    <th className="text-center p-4 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {advances.map((advance) => (
                                    <tr key={advance._id} className="border-b border-slate-200 hover:bg-slate-50">
                                        <td className="p-4 font-medium text-slate-900">{advance.employee_name}</td>
                                        <td className="p-4 text-right font-medium text-slate-900">${advance.amount?.toLocaleString()}</td>
                                        <td className="p-4 text-slate-700 max-w-xs truncate">{advance.reason}</td>
                                        <td className="p-4 text-slate-700">
                                            {new Date(advance.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="p-4 text-center">
                                            <Badge className={`rounded-none ${advance.status === 'pending_deduction' ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-green-100 text-green-800 border-green-200'}`}>
                                                {advance.status?.replace('_', ' ')}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                                {advances.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center text-slate-500">
                                            No advances found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default Advances;
